<template>
  <fragment>
    <b-card bg-variant="light">
      <b-form-input
        v-model="criteria"
        :placeholder="$t('table.filter.search_box')"
        class="p-3 mb-2 rounded-pill"
        v-if="!options.filter((option) => option.value === criteria).length > 0"
        debounce="200"
      />

      <b-form-group>
        <template v-slot:label>
          <b-icon icon="funnel"></b-icon> {{ $t('table.filter.filter_label') }}
        </template>

        <b-button-group size="sm">
          <b-button
            pill
            v-for="(option, index) in options"
            :key="index"
            :value="option.value"
            @click="criteria = $event.target.value"
            :aria-label="$t('table.filter.filter_table_help')"
            :variant="criteria === option.value ? 'primary' : ''"
            >{{ option.text }}</b-button
          >
        </b-button-group>
      </b-form-group>

      <b-button
        v-if="this.criteria"
        variant="outline-danger"
        @click="criteria = ''"
      >
        <b-icon-x-circle></b-icon-x-circle> {{ $t('buttons.clear_filter') }}
      </b-button>
    </b-card>

    <b-table
      striped
      responsive
      show-empty
      :empty-text="$t('messages.no_projects')"
      :empty-filter-text="$t('table.filter.no_result')"
      :fields="fields"
      :per-page="perPage"
      :current-page="currentPage"
      :busy="loading"
      :filter="criteria"
      :filter-function="filterTable"
      sort-icon-left
      :items="projects"
      @row-clicked="toggleTableRow"
      @filtered="onFiltered"
      ref="table"
    >
      <template v-slot:table-busy>
        <div class="text-center">
          <b-spinner
            variant="primary"
            class="align-middle"
            :label="$t('messages.loading')"
          ></b-spinner>
        </div>
      </template>

      <template v-slot:cell(title)="data">
        <fragment>
          <h5>{{ data.item.projectData.projectTitle }}</h5>
          <b-badge
            pill
            variant="light"
            v-if="data.item.createDate"
            class="p-2 mt-2 projectBadge"
            ><b-icon
              icon="person-plus-fill"
              aria-label="Published date"
              class="mr-1"
            />{{ data.item.createDate | dateReadable }}</b-badge
          >
          <b-badge
            pill
            variant="light"
            v-if="data.item.projectData.location"
            class="p-2 projectBadge"
            ><b-icon
              icon="geo-alt-fill"
              :aria-label="$t('table.header.location')"
              class="mr-1"
            />{{ data.item.projectData.location }}</b-badge
          >
        </fragment>
      </template>

      <template v-slot:cell(location)="data">{{
        data.item.projectData.location
      }}</template>

      <template v-slot:cell(buyerName)="data">{{
        data.item.projectData.buyerName
      }}</template>

      <template v-slot:cell(status)="data">
        <StatusIcon
          :status="data.item.status"
          :daysToExpired="data.item.daysToExpired"
        />
      </template>

      <template v-slot:cell(activity)="data">
        <b-button
          @click="data.toggleDetails"
          v-if="data.item.status === 'open'"
          :variant="data.detailsShowing ? '' : 'primary'"
          >{{
            data.detailsShowing
              ? $t('buttons.close')
              : $t('buttons.respond_request')
          }}</b-button
        >

        <b-button
          @click="data.toggleDetails"
          v-if="
            data.item.status === 'pending' || data.item.status === 'expired'
          "
          :variant="data.detailsShowing ? '' : 'primary'"
          >{{
            data.detailsShowing ? $t('buttons.close') : $t('buttons.add_quote')
          }}</b-button
        >

        <b-button
          v-if="data.item.status === 'quoted' || data.item.status === 'repwon'"
          @click="data.toggleDetails"
          :variant="data.detailsShowing ? '' : 'primary'"
          >{{
            data.detailsShowing
              ? $t('buttons.close')
              : $t('buttons.report_quote')
          }}</b-button
        >

        <b-button
          v-if="
            data.item.status === 'lost' ||
              data.item.status === 'declined' ||
              data.item.status === 'invalid'
          "
          @click="data.toggleDetails"
          >{{
            data.detailsShowing ? $t('buttons.close') : $t('buttons.change')
          }}</b-button
        >
      </template>

      <template v-slot:row-details="data">
        <ProjectDetails
          v-if="data.item.status === 'open' || data.item.status === 'invalid'"
          :id="data.item.projectData.projectId"
          :leadId="data.item.id"
          :status="data.item.status"
          :location="data.item.projectData.location"
          :currency="data.item.currency"
          :daysLeft="data.item.daysToExpired"
          :provisionPercent="data.item.provisionPercent"
          :expireFee="data.item.expireFee"
          :prePayment="data.item.prepayment"
          :leadPrice="data.item.leadprice"
        />

        <QuoteDetails
          v-if="
            data.item.status === 'won' ||
              data.item.status === 'lost' ||
              data.item.status === 'quoted' ||
              data.item.status === 'repwon' ||
              data.item.status === 'declined'
          "
          :id="data.item.id"
          :projectId="data.item.projectData.projectId"
          :status="data.item.status"
          :vat="data.item.vat"
          :currency="data.item.currency"
          :buyerName="data.item.projectData.buyerName"
          :buyerEmail="data.item.projectData.buyerEmail"
          :buyerPhone="data.item.projectData.buyerPhone"
          :location="data.item.projectData.location"
          :provisionPercent="data.item.provisionPercent"
          :debitDate="data.item.debitDate"
          @submit="refreshTableData"
        />

        <QuoteForm
          v-if="
            data.item.status === 'pending' || data.item.status === 'expired'
          "
          :id="data.item.id"
          :projectId="data.item.projectData.projectId"
          :status="data.item.status"
          :standalone="false"
          :extendable="data.item.extendable"
          :vat="data.item.vat"
          :currency="data.item.currency"
          :daysLeft="data.item.daysToExpired"
          :provisionPercent="data.item.provisionPercent"
          :expireFee="data.item.expireFee"
          :buyerName="data.item.projectData.buyerName"
          :location="data.item.projectData.location"
          :email="data.item.projectData.buyerEmail"
          :telephone="data.item.projectData.buyerPhone"
          :complainable="data.item.complainable"
          @submit="refreshTableData"
        />

        <b-button size="sm" @click="data.toggleDetails">{{
          $t('buttons.close')
        }}</b-button>
      </template>
    </b-table>

    <b-pagination
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      aria-controls="project-table"
      align="center"
      pills
    ></b-pagination>

    <b-button pill class="action-btn" href="#app" v-if="scrollPositionY > 90">
      <b-icon icon="chevron-bar-up"></b-icon> {{ $t('user.scroll_top') }}
    </b-button>
  </fragment>
</template>
<script>
import api from '../api/api';
import StatusIcon from '@/components/StatusIcon.vue';
import QuoteDetails from '@/components/QuoteDetails.vue';
import QuoteForm from '@/components/QuoteForm.vue';
import ProjectDetails from '@/components/ProjectDetails.vue';

export default {
  name: 'ProjectsTable',
  components: {
    StatusIcon,
    QuoteDetails,
    QuoteForm,
    ProjectDetails,
  },
  data() {
    return {
      scrollPositionY: 0,
      //Related to table
      loading: false,
      projects: [],
      perPage: 15,
      currentPage: 1,
      rowsFiltered: null, //Used to store number of table rows when a filter is applied
      //Related to table filtering
      criteria:
        this.$route.params.filter && this.$route.params.filter !== '0'
          ? decodeURIComponent(this.$route.params.filter)
          : localStorage.getItem('filter')
          ? localStorage.getItem('filter')
          : '',
    };
  },
  watch: {
    criteria: {
      immediate: true,
      handler() {
        localStorage.setItem('filter', this.criteria);

        if (!this.criteria) {
          this.rowsFiltered = null;
        }
      },
    },
  },
  computed: {
    fields() {
      return [
        { key: 'title', label: this.$t('table.header.title'), sortable: true },
        {
          key: 'location',
          label: this.$t('table.header.location'),
          class: 'd-none',
        },
        {
          key: 'buyerName',
          label: this.$t('table.header.buyer'),
          sortable: true,
          class: 'd-none d-md-table-cell',
        },
        {
          key: 'status',
          label: this.$t('table.header.status'),
          sortable: true,
        },
        {
          key: 'activity',
          label: this.$t('table.header.step'),
          sortable: false,
        },
      ];
    },
    rows() {
      return this.criteria ? this.rowsFiltered : this.projects.length;
    },
    options() {
      return [
        { text: this.$t('table.filter.new_projects'), value: 'open' },
        {
          text: this.$t('table.filter.awaiting_quote'),
          value: 'pending,expired,invalid',
        },
        { text: this.$t('table.filter.quoted'), value: 'quoted' },
        { text: this.$t('table.filter.repwon'), value: 'repwon,declined' },
        { text: this.$t('table.filter.won'), value: 'won' },
        { text: this.$t('table.filter.lost'), value: 'lost' },
      ];
    },
  },
  mounted() {
    this.getProvisionLeads();
    window.addEventListener('scroll', this.setScrollPosition, true);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.setScrollPosition, true);
  },
  methods: {
    setScrollPosition(e) {
      this.scrollPositionY = window.scrollY;
    },
    refreshTableData() {
      setTimeout(() => this.getProvisionLeads(), 10000);
      //this.$refs.table.refresh()
    },
    toggleTableRow(item) {
      this.$set(item, '_showDetails', !item._showDetails);
    },
    filterTable(row, filter) {
      if (this.options.filter((option) => option.value === filter).length > 0) {
        //Table filter is active
        return filter.split(',').includes(row.status);
      } else {
        //Custom search
        return (
          row.status.toLowerCase() !== 'open' &&
          (row.projectData.projectTitle.toLowerCase().includes(
            filter
              .toLowerCase()
              .trimStart()
              .trimEnd(),
          ) ||
            row.projectData.buyerEmail.toLowerCase().includes(
              filter
                .toLowerCase()
                .trimStart()
                .trimEnd(),
            ) ||
            row.projectData.buyerName.toLowerCase().includes(
              filter
                .toLowerCase()
                .trimStart()
                .trimEnd(),
            ) ||
            row.projectData.location.toLowerCase().includes(
              filter
                .toLowerCase()
                .trimStart()
                .trimEnd(),
            ) ||
            row.projectData.buyerPhone.includes(filter.trimStart().trimEnd()) ||
            row.projectData.projectId ===
              parseInt(filter.trimStart().trimEnd()))
        );
      }
    },
    onFiltered(filteredItems) {
      this.rowsFiltered = filteredItems.length;
      this.currentPage = 1;
    },
    getProvisionLeads() {
      this.loading = true;

      api
        .getProvisionLeads()
        .then((res) => {
          this.projects = res.data;
        })
        .finally(() => (this.loading = false));
    },
  },
};
</script>
<style lang="scss" src="../assets/scss/projectsTable.scss" scoped />
